import { useEffect, useState } from 'react';
import StatusDot, { StatusDotColor } from '../common/StatusDot';
import { networkService } from '@/services/network.service';
import { useMessagingStore } from '@/store/messaging.store';

type ConnectionState = 'live' | 'degraded' | 'offline';

const STATE_CONFIG: Record<ConnectionState, { color: StatusDotColor; label: string; pulse: boolean }> = {
  live: { color: 'green', label: 'Live', pulse: true },
  degraded: { color: 'yellow', label: 'Reconnecting', pulse: false },
  offline: { color: 'red', label: 'Offline', pulse: false },
};

/**
 * Connection / freshness indicator for the desktop status bar.
 * Combines device network state (networkService — Capacitor Network on
 * native, navigator.onLine on web) with the global messaging socket state
 * (set by MessagingSocketProvider, mounted in MainLayout):
 *   - offline  → device has no network
 *   - degraded → network up but realtime socket disconnected
 *   - live     → network up + realtime socket connected
 */
export default function ConnectionStatus({ className = '' }: { className?: string }) {
  const socketConnected = useMessagingStore((s) => s.isConnected);
  const [online, setOnline] = useState(networkService.isOnline());

  useEffect(() => {
    const removeListener = networkService.addListener((connected) => setOnline(connected));
    return () => {
      removeListener();
    };
  }, []);

  const state: ConnectionState = !online ? 'offline' : socketConnected ? 'live' : 'degraded';
  const { color, label, pulse } = STATE_CONFIG[state];

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs text-theme-tertiary ${className}`.trim()}
      title={`Real-time updates: ${label.toLowerCase()}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <StatusDot color={color} size="xs" pulse={pulse} />
      <span>{label}</span>
    </span>
  );
}
